import {
  LoginWithRedirectOptions,
  LogoutOptions,
  TIDContext,
  TIDContextState,
} from './TIDContext';
import { reducer } from './reducer';
import { initialTIDAuthState } from './state';
import { PersistentStore, TIDClient } from '@trimble-oss/trimble-id-react';
import { AuthState } from './types/AuthState';
import type { AuthClient } from './types/AuthClient';
import {
  createSignal,
  createMemo,
  onMount,
  ParentProps,
  useContext,
  createEffect,
  ParentComponent,
} from 'solid-js';
import { TIDUser } from './types/TIDUser';
import { hasAuthParams } from './utils/utils';
import { useReducer } from './utils/useReducer';

export interface TIDProviderProps extends ParentProps {
  /**
   * The URL for the Trimble Identity OpenID well known configuration endpoint
   * Staging: https://stage.id.trimblecloud.com/.well-known/openid-configuration
   * Production: https://id.trimble.com/.well-known/openid-configuration
   * @type {string}
   */
  configurationEndpoint?: string;
  /**
   * Client id of the application created in trimble developer console
   * @type {string}
   */
  clientId?: string;
  /**
   * The URL to which Trimble Identity should redirect after successfully authenticating a user
   * @type {string}
   */
  redirectUrl?: string;
  /**
   * The URL to which Trimble Identity should redirect after successfully logout a user
   * @type {string}
   */
  logoutRedirectUrl?: string;
  /**
   * The type of credentials you want (openID, or application_name)
   * @type {string[]}
   */
  scopes?: string[];
  /**
   * Type persistent you want the user and token to be store
   * in-memory - This one will only persist will the user stays in the page
   * localStorage - This persistent doesn't have expiration date
   * sessionStorage - This one is cleared when the page session ends
   * @type {PersistentStore}
   */
  persistentStore?: PersistentStore;
  /**
   * Auth client instance (`TIDClient` or any {@link AuthClient}, e.g. from
   * `createTIDAuthClient`). When provided, config props on the
   * provider are ignored.
   */
  tidClient?: AuthClient;
  /**
   * When the redirect callback occur this function will be call once the user is login
   * using the TIDClient.
   * This could allow you to redirect the user into another page after the login happen
   * @param {AuthState} authState - Object contain the state returned from TID
   * @example Redirect the user into the dashboard page
   * <TIDProvider onRedirectCallback={()=>{router.navigate('/dashboard')}}>
   */
  onRedirectCallback?(authState: AuthState): void;
  /**
   * When the redirect callback occur if this flag is set to true the TIDProvider will
   * check if the redirect url match the current url, if it doesn't match it will not
   * authenticate the user.
   * If this flag is set to false the TIDProvider will just check if the url contain the
   * auth params and will authenticate the user.
   * @type {boolean}
   *
   * @example Flag set to true and the url doesn't match
   * <TIDProvider checkRedirectUrlMatch={true}>
   * // url: https://localhost:3000/dashboard
   * This will not authenticate the user or try to handle the callback
   *
   * @example Flag set to true and the url match but doesn't contain the auth params
   * <TIDProvider checkRedirectUrlMatch={true}>
   * // url: https://localhost:3000/dashboard?state=123
   * This will not authenticate the user or try to handle the callback
   *
   * @example Flag set to true and the url match and contain the auth params
   * <TIDProvider checkRedirectUrlMatch={true}>
   * // url: https://localhost:3000/dashboard?code=123&state=123
   * This will authenticate the user and try to handle the callback
   *
   * @example Flag set to false
   * <TIDProvider checkRedirectUrlMatch={false}>
   * // url: https://localhost:3000/dashboard
   * This will not authenticate the user because the url contain does contain the auth params
   *
   * @example Flag set to false
   * <TIDProvider checkRedirectUrlMatch={false}>
   * // url: https://localhost:3000/dashboard?code=123&state=123
   * This will authenticate the user and try to handle the callback
   */
  checkRedirectUrlMatch?: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isEmptyObject: (object?: Record<string, any>) => boolean = (object) => {
  if (object == null || Object.keys(object).length <= 0) {
    return true;
  }
  const keys = Object.keys(object);
  for (const key of keys) {
    if (object[key] != null && object[key] !== '') {
      return false;
    }
  }
  return true;
};

const hasClientConfigProps = (
  clientConfig: Record<'config' | 'persistentOptions', object>
): boolean => {
  const isConfigEmpty = isEmptyObject(clientConfig.config);
  const isPersistentOptionsEmpty = isEmptyObject(
    clientConfig.persistentOptions
  );

  return !isConfigEmpty || !isPersistentOptionsEmpty;
};

export const TIDProvider: ParentComponent<TIDProviderProps> = (props) => {
  const context = useContext(TIDContext);

  const [currentState, dispatch] = useReducer(reducer, initialTIDAuthState);

  const [client, setClient] = createSignal<AuthClient>();

  onMount(() => {
    if (context != null) {
      throw new Error('TID Provider already defined');
    }

    setClient(props.tidClient ?? new TIDClient(clientConfig()));
  });

  const clientConfig = createMemo(() => {
    const config = {
      config: {
        configurationEndpoint: props.configurationEndpoint ?? '',
        clientId: props.clientId ?? '',
        redirectUrl: props.redirectUrl ?? '',
        logoutRedirectUrl: props.logoutRedirectUrl ?? '',
        scopes: props.scopes ?? [''],
      },
      persistentOptions: {
        persistentStore:
          props.persistentStore ?? ('in-memory' as PersistentStore),
      },
    };
    return config;
  });

  let ignoreInit = false;

  //TODO: Does this do what it's supposed to do?
  const redirectUrlMatch = createMemo(() =>
    props.checkRedirectUrlMatch ? client()!.getRedirectUrl() : undefined
  );

  createEffect(() => {
    if (ignoreInit) {
      return;
    }
    if (props.tidClient != null && hasClientConfigProps(clientConfig())) {
      console.warn(
        'When TID client is pass as prop, any client configuration property sent directly to the TID Provider component will be ignored'
      );
    }
    ignoreInit = true;

    (async () => {
      try {
        let user: TIDUser | undefined;
        if (hasAuthParams(undefined, redirectUrlMatch())) {
          const { authState } = await client()!.handleCallback();
          user = await client()!.getUser();
          if (props.onRedirectCallback != null) {
            props.onRedirectCallback(authState);
          }
        } else {
          await client()!.loadUserSession();
          user = await client()!.getUser();
        }
        dispatch({ type: 'INIT', user });
      } catch (error) {
        let errorMessage = error as Error;
        if (typeof error === 'string') {
          errorMessage = new Error(error);
        }
        console.log('Received error', error);
        dispatch({ type: 'ERROR', error: errorMessage });
      }
    })().catch((error: unknown) => {
      console.log('Received error', error);
      dispatch({ type: 'ERROR', error: error as Error });
    });
  });

  /**
   * Get the access token from the TID Client
   */
  const getAccessTokenSilently: () => Promise<string> = async () => {
    const token = await client()!.getAccessTokenSilently();
    const user = await client()!.getUser();
    dispatch({
      type: 'GET_ACCESS_TOKEN_COMPLETE',
      user,
    });
    return token;
  };

  /**
   * Redirect the user to the TID login page
   * @param {LoginWithRedirectOptions} options - Options to be pass to the loginWithRedirect function
   */
  const loginWithRedirect = async (
    options?: LoginWithRedirectOptions
  ): Promise<void> => {
    await client()!.loginWithRedirect(options);
  };

  /**
   * Logout the user and clear data
   * @param {LogoutOptions} options - Options to be pass to the logout function
   */
  const logout = async (options?: LogoutOptions): Promise<void> => {
    await client()!.logout(options);
    if (options?.disabledAutoRedirect != null && options.disabledAutoRedirect) {
      dispatch({
        type: 'LOGOUT',
      });
    }
  };

  /**
   * This function will handle the callback from TID
   * @param {string} url - The url that will be used to handle the callback
   */
  const handleCallback = async (url?: string): Promise<AuthState> => {
    const { authState } = await client()!.handleCallback(url);
    const user = await client()!.getUser();
    dispatch({
      type: 'HANDLE_CALLBACK_COMPLETE',
      user,
    });
    return {
      authState,
    };
  };

  const contextState = createMemo<TIDContextState>(() => ({
    ...currentState,
    getAccessTokenSilently,
    loginWithRedirect,
    handleCallback,
    logout,
  }));

  return (
    <TIDContext.Provider value={contextState}>
      {props.children}
    </TIDContext.Provider>
  );
};
